import React, { useState } from 'react';
import { 
  MousePointer, Route, Trees, Hammer, Home, ShoppingBag, Factory, 
  Zap, Droplet, Shield, Flame, BookOpen, Heart, Building2, Globe 
} from 'lucide-react';

export type ToolType = 
  | 'select' 
  | 'road' 
  | 'demolish'
  | 'park'
  | 'police'
  | 'fire'
  | 'hospital'
  | 'school'
  | 'power_solar'
  | 'power_coal'
  | 'water_tower'
  | 'water_pump'
  | 'port'
  | 'airport'
  | 'central_station'
  | 'panoramic_tower'
  | 'stadium'
  | 'zone_residential_low' 
  | 'zone_residential_medium' 
  | 'zone_residential_high'
  | 'zone_commercial_low' 
  | 'zone_commercial_medium' 
  | 'zone_commercial_high'
  | 'zone_industrial_low' 
  | 'zone_industrial_medium' 
  | 'zone_industrial_high';

interface BuildToolbarProps {
  activeTool: ToolType;
  onSelectTool: (tool: ToolType) => void;
}

type CategoryType = 'select' | 'road' | 'res' | 'com' | 'ind' | 'services' | 'utilities' | 'grand_projects' | 'demolish';

export const BuildToolbar: React.FC<BuildToolbarProps> = ({ activeTool, onSelectTool }) => {
  // Local state to manage which drawer is open
  const [activeCategory, setActiveCategory] = useState<CategoryType>('select');

  const handleCategoryClick = (category: CategoryType) => {
    setActiveCategory(category);
    
    // Auto-select standard default tools inside categories
    if (category === 'select') {
      onSelectTool('select');
    } else if (category === 'road') {
      onSelectTool('road');
    } else if (category === 'demolish') {
      onSelectTool('demolish');
    } else if (category === 'res') {
      onSelectTool('zone_residential_low');
    } else if (category === 'com') {
      onSelectTool('zone_commercial_low');
    } else if (category === 'ind') {
      onSelectTool('zone_industrial_low');
    } else if (category === 'services') {
      onSelectTool('park'); // Park is default service
    } else if (category === 'utilities') {
      onSelectTool('power_solar'); // Solar is default utility
    } else if (category === 'grand_projects') {
      onSelectTool('port'); // Port is default grand project
    }
  };

  return (
    <div className="toolbar-container" style={{ display: 'flex', flexDirection: 'column', gap: '8px', pointerEvents: 'auto' }}>
      
      {/* 1. FLOATING TOOL DRAWERS (appears dynamically above the main dock) */}
      
      {/* A. RESIDENTIAL DENSITY DRAWER */}
      {activeCategory === 'res' && (
        <div className="drawer glass-card" style={{ display: 'flex', gap: '10px', padding: '10px', borderRadius: '10px', justifyContent: 'center', margin: '0 auto', border: '1px solid rgba(76, 175, 80, 0.3)' }}>
          <button className={`btn-sub-tool ${activeTool === 'zone_residential_low' ? 'active' : ''}`} onClick={() => onSelectTool('zone_residential_low')} style={{ padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'zone_residential_low' ? '#4caf50' : 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: '11px' }}>
            Bassa Densità (Villette - §10)
          </button>
          <button className={`btn-sub-tool ${activeTool === 'zone_residential_medium' ? 'active' : ''}`} onClick={() => onSelectTool('zone_residential_medium')} style={{ padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'zone_residential_medium' ? '#4caf50' : 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: '11px' }}>
            Media Densità (Appartamenti - §10)
          </button>
          <button className={`btn-sub-tool ${activeTool === 'zone_residential_high' ? 'active' : ''}`} onClick={() => onSelectTool('zone_residential_high')} style={{ padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'zone_residential_high' ? '#4caf50' : 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: '11px' }}>
            Alta Densità (Grattacieli - §10)
          </button>
        </div>
      )}

      {/* B. COMMERCIAL DENSITY DRAWER */}
      {activeCategory === 'com' && (
        <div className="drawer glass-card" style={{ display: 'flex', gap: '10px', padding: '10px', borderRadius: '10px', justifyContent: 'center', margin: '0 auto', border: '1px solid rgba(33, 150, 243, 0.3)' }}>
          <button className={`btn-sub-tool ${activeTool === 'zone_commercial_low' ? 'active' : ''}`} onClick={() => onSelectTool('zone_commercial_low')} style={{ padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'zone_commercial_low' ? '#2196f3' : 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: '11px' }}>
            Bassa Densità (Negozi - §10)
          </button>
          <button className={`btn-sub-tool ${activeTool === 'zone_commercial_medium' ? 'active' : ''}`} onClick={() => onSelectTool('zone_commercial_medium')} style={{ padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'zone_commercial_medium' ? '#2196f3' : 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: '11px' }}>
            Media Densità (Gallerie - §10)
          </button>
          <button className={`btn-sub-tool ${activeTool === 'zone_commercial_high' ? 'active' : ''}`} onClick={() => onSelectTool('zone_commercial_high')} style={{ padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'zone_commercial_high' ? '#2196f3' : 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: '11px' }}>
            Alta Densità (Torri Vetrate - §10)
          </button>
        </div>
      )}

      {/* C. INDUSTRIAL DENSITY DRAWER */}
      {activeCategory === 'ind' && (
        <div className="drawer glass-card" style={{ display: 'flex', gap: '10px', padding: '10px', borderRadius: '10px', justifyContent: 'center', margin: '0 auto', border: '1px solid rgba(255, 235, 59, 0.3)' }}>
          <button className={`btn-sub-tool ${activeTool === 'zone_industrial_low' ? 'active' : ''}`} onClick={() => onSelectTool('zone_industrial_low')} style={{ padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'zone_industrial_low' ? '#fbc02d' : 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: '11px' }}>
            Bassa Densità (Officine - §10)
          </button>
          <button className={`btn-sub-tool ${activeTool === 'zone_industrial_medium' ? 'active' : ''}`} onClick={() => onSelectTool('zone_industrial_medium')} style={{ padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'zone_industrial_medium' ? '#fbc02d' : 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: '11px' }}>
            Media Densità (Depositi - §10)
          </button>
          <button className={`btn-sub-tool ${activeTool === 'zone_industrial_high' ? 'active' : ''}`} onClick={() => onSelectTool('zone_industrial_high')} style={{ padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'zone_industrial_high' ? '#fbc02d' : 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: '11px' }}>
            Alta Densità (Fabbriche - §10)
          </button>
        </div>
      )}

      {/* D. CIVIC SERVICES DRAWER */}
      {activeCategory === 'services' && (
        <div className="drawer glass-card" style={{ display: 'flex', gap: '8px', padding: '8px', borderRadius: '10px', justifyContent: 'center', margin: '0 auto' }}>
          {/* Park */}
          <button className={`btn-drawer-item ${activeTool === 'park' ? 'active' : ''}`} onClick={() => onSelectTool('park')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'park' ? '#26a69a' : 'rgba(255,255,255,0.06)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
            <Trees size={14} style={{ color: '#4caf50' }} />
            <span>Parco (§150)</span>
          </button>
          {/* Police */}
          <button className={`btn-drawer-item ${activeTool === 'police' ? 'active' : ''}`} onClick={() => onSelectTool('police')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'police' ? '#26a69a' : 'rgba(255,255,255,0.06)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
            <Shield size={14} style={{ color: '#1565c0' }} />
            <span>Polizia (§400)</span>
          </button>
          {/* Fire */}
          <button className={`btn-drawer-item ${activeTool === 'fire' ? 'active' : ''}`} onClick={() => onSelectTool('fire')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'fire' ? '#26a69a' : 'rgba(255,255,255,0.06)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
            <Flame size={14} style={{ color: '#ff1744' }} />
            <span>Pompieri (§400)</span>
          </button>
          {/* School */}
          <button className={`btn-drawer-item ${activeTool === 'school' ? 'active' : ''}`} onClick={() => onSelectTool('school')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'school' ? '#26a69a' : 'rgba(255,255,255,0.06)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
            <BookOpen size={14} style={{ color: '#ff9100' }} />
            <span>Scuola (§500)</span>
          </button>
          {/* Hospital */}
          <button className={`btn-drawer-item ${activeTool === 'hospital' ? 'active' : ''}`} onClick={() => onSelectTool('hospital')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'hospital' ? '#26a69a' : 'rgba(255,255,255,0.06)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
            <Heart size={14} style={{ color: '#e040fb' }} />
            <span>Ospedale (§600)</span>
          </button>
        </div>
      )}

      {/* E. POWER & WATER UTILITIES DRAWER */}
      {activeCategory === 'utilities' && (
        <div className="drawer glass-card" style={{ display: 'flex', gap: '8px', padding: '8px', borderRadius: '10px', justifyContent: 'center', margin: '0 auto' }}>
          {/* Solar Plant */}
          <button className={`btn-drawer-item ${activeTool === 'power_solar' ? 'active' : ''}`} onClick={() => onSelectTool('power_solar')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'power_solar' ? '#26a69a' : 'rgba(255,255,255,0.06)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
            <Zap size={14} style={{ color: '#ffeb3b' }} />
            <span>Solare (§450)</span>
          </button>
          {/* Coal Plant */}
          <button className={`btn-drawer-item ${activeTool === 'power_coal' ? 'active' : ''}`} onClick={() => onSelectTool('power_coal')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'power_coal' ? '#26a69a' : 'rgba(255,255,255,0.06)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
            <Zap size={14} style={{ color: '#78909c' }} />
            <span>Carbone (§800)</span>
          </button>
          {/* Water Tower */}
          <button className={`btn-drawer-item ${activeTool === 'water_tower' ? 'active' : ''}`} onClick={() => onSelectTool('water_tower')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'water_tower' ? '#26a69a' : 'rgba(255,255,255,0.06)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
            <Droplet size={14} style={{ color: '#03a9f4' }} />
            <span>Torre Idrica (§300)</span>
          </button>
          {/* Water Pump */}
          <button className={`btn-drawer-item ${activeTool === 'water_pump' ? 'active' : ''}`} onClick={() => onSelectTool('water_pump')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'water_pump' ? '#26a69a' : 'rgba(255,255,255,0.06)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
            <Droplet size={14} style={{ color: '#00e5ff' }} />
            <span>Pompa Idrica (§550)</span>
          </button>
        </div>
      )}

      {/* F. GRAND PROJECTS DRAWERS (Phase 9 & 12) */}
      {activeCategory === 'grand_projects' && (
        <div className="drawer glass-card" style={{ display: 'flex', gap: '8px', padding: '8px', borderRadius: '10px', justifyContent: 'center', margin: '0 auto' }}>
          {/* Port */}
          <button className={`btn-drawer-item ${activeTool === 'port' ? 'active' : ''}`} onClick={() => onSelectTool('port')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'port' ? '#26a69a' : 'rgba(255,255,255,0.06)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
            <Globe size={14} style={{ color: '#0288d1' }} />
            <span>Porto Fluviale (§800)</span>
          </button>
          {/* Airport */}
          <button className={`btn-drawer-item ${activeTool === 'airport' ? 'active' : ''}`} onClick={() => onSelectTool('airport')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'airport' ? '#26a69a' : 'rgba(255,255,255,0.06)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
            <Globe size={14} style={{ color: '#ffb74d' }} />
            <span>Aeroporto (§1400)</span>
          </button>
          {/* Central Station */}
          <button className={`btn-drawer-item ${activeTool === 'central_station' ? 'active' : ''}`} onClick={() => onSelectTool('central_station')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'central_station' ? '#26a69a' : 'rgba(255,255,255,0.06)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
            <Globe size={14} style={{ color: '#ab47bc' }} />
            <span>Stazione Centrale (§1800)</span>
          </button>
          {/* Panoramic Tower */}
          <button className={`btn-drawer-item ${activeTool === 'panoramic_tower' ? 'active' : ''}`} onClick={() => onSelectTool('panoramic_tower')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'panoramic_tower' ? '#26a69a' : 'rgba(255,255,255,0.06)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
            <Globe size={14} style={{ color: '#26a69a' }} />
            <span>Torre Panoramica (§3000)</span>
          </button>
          {/* Stadium */}
          <button className={`btn-drawer-item ${activeTool === 'stadium' ? 'active' : ''}`} onClick={() => onSelectTool('stadium')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', border: 'none', borderRadius: '6px', background: activeTool === 'stadium' ? '#26a69a' : 'rgba(255,255,255,0.06)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
            <Globe size={14} style={{ color: '#4caf50' }} />
            <span>Grande Stadio (§5000)</span>
          </button>
        </div>
      )}


      {/* 2. MAIN TOOLBAR DOCK (bottom bar) */}
      <div className="toolbar-dock glass-card" style={{ display: 'flex', gap: '6px', padding: '6px', borderRadius: '12px', background: 'rgba(38, 50, 56, 0.85)' }}>
        
        {/* EXPLORE / SELECT */}
        <button 
          className={`btn-tool ${activeCategory === 'select' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('select')}
          title="Ispeziona ed esplora la città (Gratuito)"
        >
          <MousePointer size={20} />
          <span>Esplora</span>
        </button>

        {/* ROAD */}
        <button 
          className={`btn-tool ${activeCategory === 'road' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('road')}
          title="Costruisci Strada Locale (Costo: 25 §)"
        >
          <Route size={20} />
          <span>Strada</span>
        </button>

        {/* RESIDENZIALE (Drawer trigger) */}
        <button 
          className={`btn-tool ${activeCategory === 'res' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('res')}
          style={activeCategory === 'res' ? { borderTop: '2px solid #4caf50' } : {}}
          title="Zonizzazione Residenziale"
        >
          <Home size={20} style={{ color: '#4caf50' }} />
          <span style={{ color: '#4caf50' }}>Residenziale</span>
        </button>

        {/* COMMERCIALE (Drawer trigger) */}
        <button 
          className={`btn-tool ${activeCategory === 'com' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('com')}
          style={activeCategory === 'com' ? { borderTop: '2px solid #2196f3' } : {}}
          title="Zonizzazione Commerciale"
        >
          <ShoppingBag size={20} style={{ color: '#2196f3' }} />
          <span style={{ color: '#2196f3' }}>Commerciale</span>
        </button>

        {/* INDUSTRIALE (Drawer trigger) */}
        <button 
          className={`btn-tool ${activeCategory === 'ind' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('ind')}
          style={activeCategory === 'ind' ? { borderTop: '2px solid #ffeb3b' } : {}}
          title="Zonizzazione Industriale"
        >
          <Factory size={20} style={{ color: '#ffd54f' }} />
          <span style={{ color: '#ffd54f' }}>Industriale</span>
        </button>

        {/* CIVIC SERVICES (Drawer trigger) */}
        <button 
          className={`btn-tool ${activeCategory === 'services' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('services')}
          style={activeCategory === 'services' ? { borderTop: '2px solid #e040fb' } : {}}
          title="Pubblici Servizi e Parchi"
        >
          <Building2 size={20} style={{ color: '#e040fb' }} />
          <span style={{ color: '#e040fb' }}>Servizi</span>
        </button>

        {/* UTILITIES (Drawer trigger) */}
        <button 
          className={`btn-tool ${activeCategory === 'utilities' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('utilities')}
          style={activeCategory === 'utilities' ? { borderTop: '2px solid #00bcd4' } : {}}
          title="Centrali Elettriche e Risorse Idriche"
        >
          <Zap size={20} style={{ color: '#00bcd4' }} />
          <span style={{ color: '#00bcd4' }}>Utilità</span>
        </button>

        {/* GRAND PROJECTS (Drawer trigger - Phase 9 & 12) */}
        <button 
          className={`btn-tool ${activeCategory === 'grand_projects' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('grand_projects')}
          style={activeCategory === 'grand_projects' ? { borderTop: '2px solid #0288d1' } : {}}
          title="Grandi Opere e Monumenti dell'Era"
        >
          <Globe size={20} style={{ color: '#0288d1' }} />
          <span style={{ color: '#0288d1' }}>Opere</span>
        </button>

        {/* DEMOLISH */}
        <button 
          className={`btn-tool ${activeCategory === 'demolish' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('demolish')}
          title="Demolisci edifici, strade o zone (Costo: 10 §)"
        >
          <Hammer size={20} style={{ color: '#ff1744' }} />
          <span>Demolisci</span>
        </button>
      </div>
    </div>
  );
};
